import React, { useState } from "react";
import Select from "~/components/shared/Select";

interface Option {
  label: string;
  value: string;
}

interface SearchableInputSelectProps {
  name: string;
  value?: string;
  options: Option[];
  placeholder?: string;
  required?: boolean;
  isMulti?: boolean;
  onChange?: (value: string | string[]) => void;
}

const SearchableInputSelect: React.FC<SearchableInputSelectProps> = ({
  name,
  value,
  options,
  placeholder = "Select an option...",
  required = false,
  isMulti = false,
  onChange,
}) => {
  const [selectedValue, setSelectedValue] = useState(() => {
    if (isMulti && value) {
      const values = value.split("\n").filter((value) => value.trim());
      return options.filter((option) => values.includes(option.value));
    } else if (value) {
      return options.find((option) => option.value === value) || null;
    }
    return null;
  });

  const handleChange = (selected: any) => {
    setSelectedValue(selected);

    if (onChange) {
      if (isMulti) {
        const values = selected ? selected.map((item: Option) => item.value) : [];
        onChange(values);
      } else {
        onChange(selected ? selected.value : "");
      }
    }
  };

  return (
    <>
      <Select
        name={`${name}_display`}
        value={selectedValue}
        options={options}
        placeholder={placeholder}
        isSearchable={true}
        isClearable={!required}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        menuPlacement="bottom"
        onChange={handleChange}
        menuPosition="fixed"
        styles={{
          container: (provided) => ({
            ...provided,
            minHeight: "2.5rem",
          }),
          control: (provided, state) => ({
            ...provided,
            minHeight: "2.5rem",
            cursor: "pointer",
            borderColor: state.isFocused ? "var(--gray-800)" : "var(--gray-600)",
            boxShadow: state.isFocused ? "0 0 0 1px var(--gray-800)" : "none",
            "&:hover": {
              borderColor: "var(--gray-800)",
            },
          }),
          option: (provided, state) => ({
            ...provided,
            cursor: "pointer",
            backgroundColor: state.isSelected
              ? "var(--blue-600)"
              : state.isFocused
                ? "var(--gray-100)"
                : "white",
            color: state.isSelected ? "white" : "var(--charcoal-800)",
            "&:hover": {
              backgroundColor: state.isSelected ? "var(--blue-600)" : "var(--gray-100)",
            },
          }),
          indicatorSeparator: (provided) => ({
            ...provided,
            display: "none",
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            alignItems: "start",
          }),
          valueContainer: (provided) => ({
            ...provided,
            overflow: "initial",
          }),
          multiValue: (provided) => ({
            ...provided,
            backgroundColor: "var(--gray-200)",
          }),
          multiValueLabel: (provided) => ({
            ...provided,
            color: "var(--charcoal-800)",
          }),
        }}
      />
      <input
        type="hidden"
        name={name}
        value={
          isMulti && selectedValue && Array.isArray(selectedValue)
            ? selectedValue.map((item: Option) => item.value).join("\n")
            : selectedValue && !Array.isArray(selectedValue)
              ? (selectedValue as Option).value
              : ""
        }
      />
    </>
  );
};

export default SearchableInputSelect;
